import { useQueryClient } from '@tanstack/vue-query'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { authUseCases } from '@/application/auth/auth.use-cases'
import { authToken } from '@/core/api/auth-token'
import { useOutletContextStore } from '@/stores/outlet-context.store'
import { useDashboardFilterStore } from '@/stores/dashboard-filter.store'
import type { AuthTenant, AuthUser } from '@/domain/entities/auth.entity'

/** Tahap login: perlu memilih tenant setelah langkah-1. */
type PendingLogin = { loginToken: string; tenants: AuthTenant[] }

export const useAuthStore = defineStore('auth', () => {
  const queryClient = useQueryClient()
  const user = ref<AuthUser | null>(null)
  const pendingLogin = ref<PendingLogin | null>(null)
  const refreshToken = ref<string | null>(null)
  const submitting = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)
  const hasToken = computed(() => Boolean(authToken.get()))
  const permissions = computed(() => user.value?.permissions ?? [])
  const needsTenantSelection = computed(() => pendingLogin.value !== null)

  /** Cek izin RBAC; superadmin selalu lolos. */
  function can(permission: string): boolean {
    if (!user.value) return false
    if (user.value.isSuperadmin) return true
    return user.value.permissions.includes(permission)
  }

  /** Lolos bila superadmin, atau punya SALAH SATU izin. Array kosong = true. */
  function canAny(perms: readonly string[]): boolean {
    if (!user.value) return false
    if (user.value.isSuperadmin) return true
    if (perms.length === 0) return true
    return perms.some((p) => user.value!.permissions.includes(p))
  }

  /** Lolos bila superadmin, atau punya SEMUA izin. Array kosong = true. */
  function canAll(perms: readonly string[]): boolean {
    if (!user.value) return false
    if (user.value.isSuperadmin) return true
    return perms.every((p) => user.value!.permissions.includes(p))
  }

  function toMessage(e: unknown, fallback: string): string {
    return e instanceof Error && e.message ? e.message : fallback
  }

  /** Langkah 1. Jika hanya 1 tenant, langsung lanjut ke langkah 2. */
  async function login(username: string, password: string): Promise<void> {
    submitting.value = true
    error.value = null
    try {
      const result = await authUseCases.login(username, password)
      if (result.tenants.length === 0) {
        throw new Error('Akun ini belum terhubung ke tenant mana pun.')
      }
      if (result.tenants.length === 1) {
        // 1 tenant (mis. owner) → langsung masuk, tak perlu memilih.
        await completeSelection(result.loginToken, result.tenants[0].id)
      } else {
        // Banyak tenant (mis. superadmin) → tampilkan tenant picker.
        pendingLogin.value = result
      }
    } catch (e) {
      error.value = toMessage(e, 'Login gagal. Periksa username & password.')
      throw e
    } finally {
      submitting.value = false
    }
  }

  /** Langkah 2 (dipanggil dari picker tenant). */
  async function chooseTenant(tenantId: string): Promise<void> {
    if (!pendingLogin.value) return
    submitting.value = true
    error.value = null
    try {
      await completeSelection(pendingLogin.value.loginToken, tenantId)
    } catch (e) {
      error.value = toMessage(e, 'Gagal memilih tenant.')
      throw e
    } finally {
      submitting.value = false
    }
  }

  async function completeSelection(loginToken: string, tenantId: string): Promise<void> {
    const session = await authUseCases.selectTenant(loginToken, tenantId)
    authToken.set(session.accessToken)
    refreshToken.value = session.refreshToken ?? null
    pendingLogin.value = null
    await fetchMe()
  }

  /** Ambil profil + permission (bootstrap sesi & setelah login). */
  async function fetchMe(): Promise<void> {
    user.value = await authUseCases.me()
  }

  /** Dipanggil saat app start: pulihkan sesi dari token tersimpan. */
  async function bootstrap(): Promise<void> {
    if (!authToken.get() || user.value) return
    try {
      await fetchMe()
    } catch {
      clearSession()
    }
  }

  async function logout(): Promise<void> {
    try {
      await authUseCases.logout(refreshToken.value ?? undefined)
    } catch {
      /* tetap lanjut membersihkan sesi lokal meski request logout gagal */
    } finally {
      clearSession()
    }
  }

  function clearSession(): void {
    authToken.clear()
    user.value = null
    pendingLogin.value = null
    refreshToken.value = null
    error.value = null

    // Reset store state (outlet context & dashboard filter)
    useOutletContextStore().setOutlet('')
    useDashboardFilterStore().resetFilter()

    // Hapus seluruh cache TanStack Query supaya user baru
    // tidak melihat data milik sesi sebelumnya (mis. spv melihat
    // semua outlet karena cache superadmin belum kedaluwarsa).
    queryClient.clear()

    // Clear seluruh browser storage (localStorage & sessionStorage)
    try {
      const rememberMe = localStorage.getItem('pos_remember_me')
      const rememberedUsername = localStorage.getItem('pos_remembered_username')
      const rememberedPassword = localStorage.getItem('pos_remembered_password')

      localStorage.clear()
      sessionStorage.clear()

      if (rememberMe === 'true') {
        localStorage.setItem('pos_remember_me', 'true')
        if (rememberedUsername) localStorage.setItem('pos_remembered_username', rememberedUsername)
        if (rememberedPassword) localStorage.setItem('pos_remembered_password', rememberedPassword)
      }
    } catch {
      /* ignore if restricted */
    }
  }

  function cancelTenantSelection(): void {
    pendingLogin.value = null
    error.value = null
  }

  return {
    user,
    pendingLogin,
    submitting,
    error,
    isAuthenticated,
    hasToken,
    permissions,
    needsTenantSelection,
    can,
    canAny,
    canAll,
    login,
    chooseTenant,
    fetchMe,
    bootstrap,
    logout,
    clearSession,
    cancelTenantSelection,
  }
})
